import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  NgZone,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { isMac, isNativeWindow } from '@local/common-web';
import { PopupRef, UiIconModel } from '@local/ui-infra';
import { UntilDestroy } from '@ngneat/until-destroy';
import { KeyboardService } from '@shared/services/keyboard.service';
import { isEnterKey, KeyName, splitFileName } from '@local/ts-infra';
import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { ScrollService } from 'src/app/bar/views/results/services/scroll.service';
import { NgScrollbar } from 'ngx-scrollbar';
import { KeyboardHelperService } from '@shared/helper/keyboard-helper.service';
import { Commands, Results } from '@local/client-contracts';
import { getIconModel } from '@shared/utils/set-icon.util';
import { ResultAttachmentService } from '../../../services/result-attachment.service';
import { DragMetadata } from '@shared/directives/drag-and-drop.directive';
import { CommandsService } from 'src/app/bar/services/commands/commands.service';

export type ResultAttachmentPopupData = { attachments: Results.Attachment[]; dragMetadata: DragMetadata; downloadEnabled: boolean };

interface ViewAttachment {
  text: string;
  icon: UiIconModel;
  displayIndex: number;
  item: Results.Attachment;
  openEnabled: boolean;
}

@UntilDestroy()
@Component({
  selector: 'result-attachment-popup',
  templateUrl: './result-attachment-popup.component.html',
  styleUrls: ['./result-attachment-popup.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ResultAttachmentPopupComponent implements OnInit, OnDestroy, AfterViewInit {
  private readonly MAX_WIDTH_POPUP: number = 270;
  private readonly MIN_WIDTH_POPUP: number = 200;
  private readonly WIDTH_POPUP: number = 235;
  private readonly HIGHT_POPUP: number = 200;
  private readonly WIDTH_SCROLL: number = 10;
  readonly itemSize: number = 34;
  private scrollAttachments: ViewAttachment[];
  private scrollService: ScrollService<ViewAttachment>;
  private attachments: Results.Attachment[];
  private openEnabled: boolean;
  private isNative = isNativeWindow();
  private dragMetadata: DragMetadata;
  private keyHandler: string;
  private keyboardHelperService: KeyboardHelperService;
  viewAttachment: ViewAttachment[];
  downloadEnabled: boolean;
  widthAttachments: number;
  itemWidth: number;
  selectedIndex: number = 0;
  scrollHeight: number;

  @ViewChild(NgScrollbar) scrollbarRef: NgScrollbar;
  @ViewChild(CdkVirtualScrollViewport) scrollViewport: CdkVirtualScrollViewport;
  @ViewChildren('attachment', { read: ElementRef }) attachmentsElements: QueryList<ElementRef>;

  private get width(): number {
    if (this.downloadEnabled && this.openEnabled) {
      return this.MAX_WIDTH_POPUP;
    }
    if (this.downloadEnabled || this.openEnabled) {
      return this.WIDTH_POPUP;
    }
    return this.MIN_WIDTH_POPUP;
  }

  constructor(
    private cdr: ChangeDetectorRef,
    private keyboardService: KeyboardService,
    private ref: PopupRef<ResultAttachmentPopupComponent, ResultAttachmentPopupData>,
    private resultAttachmentService: ResultAttachmentService,
    private ngZone: NgZone,
    private commandsService: CommandsService
  ) {}

  ngOnInit(): void {
    this.initData();
    this.initKeyboard();
  }

  ngAfterViewInit(): void {
    this.initScrollService();
  }

  ngOnDestroy() {
    this.scrollService?.destroy();
    if (this.keyHandler) this.keyboardService.unregisterKeyHandler(this.keyHandler);
  }

  private initData() {
    this.attachments = this.ref.data.attachments;
    this.dragMetadata = this.ref.data.dragMetadata;
    this.downloadEnabled = this.ref.data.downloadEnabled;
    this.viewAttachment = this.scrollAttachments = this.attachments.map((a, i) => {
      {
        const icon = this.resultAttachmentService.getAttachmentIcon(a?.extension);
        const openEnabled = this.resultAttachmentService.isOpenEnabled(a) && this.isNative;
        if (openEnabled) this.openEnabled = true;
        return {
          text: splitFileName(a?.text, a?.extension),
          icon: getIconModel(icon || a.icon),
          displayIndex: i,
          item: a as Results.Attachment,
          openEnabled,
        } as ViewAttachment;
      }
    });

    this.selectedIndex = 0;
    this.widthAttachments = this.itemWidth = this.width;
    this.itemWidth -= this.WIDTH_SCROLL;
    if (this.attachments.length > 5) {
      this.widthAttachments += this.WIDTH_SCROLL;
      this.scrollHeight = this.HIGHT_POPUP;
    } else {
      this.scrollHeight = this.attachments.length * this.itemSize;
    }
  }

  private initKeyboard() {
    this.scrollService = new ScrollService(this.ngZone);
    this.keyboardHelperService = new KeyboardHelperService();
    this.keyboardHelperService.onInit(this.selectedIndex, this.scrollAttachments, this.scrollService);
    this.keyHandler = this.keyboardService.registerKeyHandler((keys, event) => {
      this.keysHandler(keys, event);
      this.keyboardHelperService.handleArrows(keys, event);
    }, 10);
  }

  private initScrollService() {
    this.scrollService.items = this.scrollAttachments;
    this.scrollService.itemHeights = Array(this.scrollAttachments.length).fill(this.itemSize);
    this.scrollService.init(this.scrollbarRef, this.attachmentsElements, this.scrollViewport);
    if (!this.scrollViewport?.elementRef?.nativeElement) {
      this.scrollService?.destroy();
    }
    this.cdr.markForCheck();
  }

  private keysHandler(keys: Array<KeyName>, event): void {
    if (isMac()) keys = keys.map((k) => (k = k === 'control' ? 'command' : k));
    const key = keys[0];

    if (keys.length === 1) {
      if (key === 'ArrowDown') {
        this.selectedIndex < this.attachments?.length ? (this.selectedIndex += 1) : null;
        this.setEvent(event);
        return;
      }

      if (key === 'ArrowUp') {
        this.selectedIndex > -1 ? (this.selectedIndex -= 1) : null;
        this.setEvent(event);
        return;
      }

      if (key === 'escape') {
        this.ref.destroy();
        this.setEvent(event);
        return;
      }

      if (isEnterKey(key) && this.selectedIndex > -1 && this.selectedIndex < this.attachments?.length) {
        this.onClick(this.attachments[this.selectedIndex], this.selectedIndex);
        this.setEvent(event);
        return;
      }
      event.stopPropagation();
      this.cdr.markForCheck();
    }
  }

  private setEvent(event) {
    event.preventDefault();
    event.stopPropagation();
    this.cdr.markForCheck();
  }

  async open(attachment: Results.Attachment) {
    const command: Commands.Command = await this.resultAttachmentService.download({
      metadata: this.dragMetadata,
      model: attachment,
    });
    if (command) {
      await this.commandsService.executeCommand(command);
    }
  }

  download(attachment: Results.Attachment) {
    if (!attachment.onDrag) return;
    this.resultAttachmentService.handleContextMenuCommand(attachment.onDrag, {
      metadata: this.dragMetadata,
      model: attachment,
    });
  }

  async onClick(attachment: Results.Attachment, index: number) {
    this.selectedIndex = index;
    this.cdr.markForCheck();
    if (this.openEnabled) {
      await this.open(attachment);
      return;
    }
    this.download(attachment);
  }
}
